import { digitsEnToFa } from '@persian-tools/persian-tools';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map, timeout } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ConfigService,
  FileEntity,
  HTTP_REQUESTS_TIMEOUT,
  Image,
} from 'src/app/core';
import { FilesService } from 'src/app/feature';
import {
  ActionResult,
  AppActionTypes,
  AppEntityAction,
  DocumentSearchEvent,
  LikeEntity,
} from '../interfaces';

@Injectable({ providedIn: 'root' })
export class DocumentService {
  private _stream$!: BehaviorSubject<FileEntity[]>;
  private _loading$!: BehaviorSubject<boolean>;
  private _plainCache: FileEntity[] = [];

  private set cache(files: FileEntity[]) {
    files.forEach((file) => {
      if (this._plainCache.findIndex((cached) => cached.id === file.id) < 0) {
        this._plainCache.push(file);
      }
    });
  }

  public get loading$(): Observable<boolean> {
    return this._loading$.pipe(catchError(() => EMPTY));
  }

  public get stream$(): Observable<FileEntity[]> {
    return this._stream$.pipe(catchError(() => EMPTY));
  }

  constructor(
    private configService: ConfigService,
    private filesService: FilesService,
    private snackBar: MatSnackBar,
    private http: HttpClient
  ) {
    this._loading$ = new BehaviorSubject(false as boolean);
    this._stream$ = new BehaviorSubject([] as FileEntity[]);
  }

  public confirm(id: number): Observable<FileEntity> {
    const url = this.configService
      .getUrl('documents.confirm')
      .replace(':id', id.toString());

    return this.http.put<FileEntity>(url, {}, { withCredentials: true }).pipe(
      timeout(HTTP_REQUESTS_TIMEOUT),
      catchError((err, caught) => {
        if (err) {
          this.snackBar.open('متاسفانه مشکلی در تایید فایل پیش آمد.', '', {
            duration: 1500,
          });
          throw err;
        }
        this.snackBar.open('فایل با موفقیت تایید شد', '', { duration: 1500 });

        return caught;
      })
    );
  }

  public unconfirm(id: number, reason: string) {
    const url = this.configService
      .getUrl('documents.unconfirm')
      .replace(':id', id.toString())
      .replace(':reason', reason);

    return this.http.put(url, {}, { withCredentials: true }).pipe(
      timeout(HTTP_REQUESTS_TIMEOUT),
      catchError((err, caught) => {
        if (err) {
          this.snackBar.open('متاسفانه مشکلی در ثبت خطا پیش آمد.', '', {
            duration: 1500,
          });

          throw err;
        }

        this.snackBar.open('خطا با موفقیت ثبت شد', '', { duration: 1500 });
        return caught;
      })
    );
  }

  /**
   * Get shared documents.
   * @param page The page you want to load.
   * @param loadCache If its true then you get the cached data into memory.
   */
  getSharedDocuments(page: number, limit?: number, loadCache?: boolean): void {
    if (loadCache) {
      this._emitStream(this._plainCache, true);
    } else {
      this.filesService
        .getSharedDocument(page, limit)
        .pipe(map((data) => this._emitLoadingMapPipe(data)))
        .subscribe(
          (data) => {
            this._emitStream(data.items);
            this._stopLoading();
          },
          (err) => {
            this._stopLoading();
          }
        );
    }
  }

  shareDocumentWithPreview(shareDto: any) {
    const url = this.configService.getUrl('documents.share');
    return this.http
      .post(url, shareDto, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  getImagesOf(id: number) {
    const url = this.configService
      .getUrl('documents.images')
      .replace(':id', id.toString());

    return this.http
      .get<Image[]>(url, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  getDocumentByHash(hash: string): Observable<FileEntity> {
    const cached = this._checkAndReturnCachedFileEntity(hash);
    if (cached) {
      return of(cached);
    }
    return this.filesService.getOneSingleFileWithHash(hash);
  }

  simpleSearch(value: string): void {
    value = digitsEnToFa(value);

    const url = this.configService
      .getUrl('documents.simpleSearch')
      .replace(':value', value);

    this.http
      .get<FileEntity[]>(url, { withCredentials: true })
      .subscribe((data) => {
        this._emitStream(data, true);
      });
  }

  complexSearch(search: DocumentSearchEvent) {
    const url = this.configService.getUrl('documents.complexSearch');
    this.http
      .post<FileEntity[]>(url, search, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
      .subscribe((data) => {
        this._emitStream(data, true);
      });
  }

  /**
   * If you get data somewhere else and now you
   * need to pipe it into stream.
   */
  public pipe(data: FileEntity[]) {
    this._emitStream(data, true);
    this._cacheNewData(data);
  }

  /**
   * Send action object to the server.
   * @param type like or dislike
   * @param id document id
   */
  public runAction(
    type: AppActionTypes,
    id: number
  ): Observable<ActionResult<LikeEntity>> {
    const action = this._generateAction(type, id);
    const url = this.configService.getUrl('documents.actions');
    return this.http
      .post<ActionResult<LikeEntity>>(url, action, {
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public getLikes(id: number): Observable<LikeEntity[]> {
    const url = this.configService
      .getUrl('documents.likes')
      .replace(':id', id.toString());
    return this.http
      .get<LikeEntity[]>(url, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  // Private methods ===========================================>

  private _generateAction(type: AppActionTypes, id: number): AppEntityAction {
    return { action: type, id };
  }

  private _emitLoadingMapPipe<T>(data: T): T {
    this._loading$.next(true);
    return data;
  }
  private _stopLoading() {
    this._loading$.next(false);
  }

  /**
   * @param newData Data.
   * @param force If you pass true the cache wil be excluded.
   */
  private _emitStream(newData: FileEntity[], force: boolean = false) {
    switch (force) {
      case true:
        this._stream$.next(newData);
        this._stopLoading();
        break;

      case false:
        this._stream$.next(this._appendCacheTo(newData));
        this._cacheNewData(newData);
        this._stopLoading();

        break;
    }
  }

  private _cacheNewData(newData: FileEntity[]) {
    this.cache = newData;
  }
  private _appendCacheTo(data: FileEntity[]) {
    return [...this._plainCache, ...data];
  }
  private _checkAndReturnCachedFileEntity(
    hash: string
  ): FileEntity | undefined {
    return this._plainCache.filter((o) => o.hash === hash)[0];
  }
}
